import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Stack, Group, Box, ScrollArea, Alert } from '@mantine/core'
import { AppButton, AppDrawer, AppIconButton, AppText } from '../../app/common/ui'
import {
  CommentInputRow,
  OneLevelCommentList,
  type PlatformCommentNode
} from '@gunslop/ui'
import { useHistory } from 'react-router-dom'
import { IoSend, IoTrashOutline, IoReturnDownForwardOutline } from 'react-icons/io5'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import type { SlopPostCommentDto } from '../../app/models/slopPost'

export type CommentsDrawerTarget = 'slop' | 'shoot'

export interface CommentsDrawerProps {
  opened: boolean;
  onClose: () => void;
  target: CommentsDrawerTarget;
  contentId?: string | null;
  /** Called when comment count changes (add/delete). */
  onCommentCountChange?: (count: number) => void;
}

function toPlatformNodes (slop: SlopPostCommentDto[]): PlatformCommentNode[] {
  return slop.map((c) => ({
    id: c.id,
    body: c.body,
    authorUsername: c.authorUsername,
    parentCommentId: c.parentCommentId,
    createdAt: c.createdAt,
    replies: (c.replies ?? []).map((r) => ({
      id: r.id,
      body: r.body,
      authorUsername: r.authorUsername,
      parentCommentId: r.parentCommentId,
      createdAt: r.createdAt,
      replies: []
    }))
  }))
}

function countThread (nodes: PlatformCommentNode[]): number {
  return nodes.reduce((n, c) => n + 1 + (c.replies?.length ?? 0), 0)
}

export function CommentsDrawer ({
  opened,
  onClose,
  target,
  contentId,
  onCommentCountChange
}: CommentsDrawerProps) {
  const history = useHistory()
  const { userStore } = useStore()
  const currentUser = userStore.user
  const isLoggedIn = !!currentUser
  const isAdmin = userStore.isAdmin

  const [comments, setComments] = useState<PlatformCommentNode[]>([])
  const [loading, setLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyingToId, setReplyingToId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [loadError, setLoadError] = useState<string | null>(null)

  const supportsReplies = target === 'slop'
  const fullPostPath = contentId
    ? (target === 'slop' ? `/slop-feed/${contentId}` : `/slop-feed/shoot/${contentId}`)
    : null

  const loadComments = useCallback(async () => {
    if (!contentId) return
    setLoading(true)
    setLoadError(null)
    try {
      if (target === 'slop') {
        const list = await agent.SlopFeed.getComments(contentId)
        setComments(toPlatformNodes(list))
      } else {
        const list = await agent.Shoots.getComments(contentId)
        setComments(list.map((c) => ({
          id: c.id,
          body: c.body,
          authorUsername: c.authorUsername,
          parentCommentId: null,
          createdAt: c.createdAt,
          replies: []
        })))
      }
    } catch {
      setComments([])
      setLoadError('Could not load comments.')
    } finally {
      setLoading(false)
    }
  }, [contentId, target])

  useEffect(() => {
    if (!opened || !contentId) return
    void loadComments()
  }, [opened, contentId, loadComments])

  useEffect(() => {
    if (!opened) {
      setNewComment('')
      setReplyText('')
      setReplyingToId(null)
      setLoadError(null)
    }
  }, [opened])

  const totalCount = useMemo(() => countThread(comments), [comments])

  const notifyCount = useCallback((nodes: PlatformCommentNode[]) => {
    onCommentCountChange?.(countThread(nodes))
  }, [onCommentCountChange])

  const handleAddTopLevel = async () => {
    if (!contentId || !newComment.trim()) return
    setSubmitting(true)
    try {
      if (target === 'slop') {
        const created = await agent.SlopFeed.addComment(contentId, newComment.trim())
        const next = [...comments, {
          id: created.id,
          body: created.body,
          authorUsername: created.authorUsername,
          parentCommentId: created.parentCommentId,
          createdAt: created.createdAt,
          replies: []
        }]
        setComments(next)
        notifyCount(next)
      } else {
        const created = await agent.Shoots.addComment(contentId, newComment.trim())
        const next = [...comments, {
          id: created.id,
          body: created.body,
          authorUsername: created.authorUsername,
          parentCommentId: null,
          createdAt: created.createdAt,
          replies: []
        }]
        setComments(next)
        notifyCount(next)
      }
      setNewComment('')
    } catch { /* toast later */ } finally {
      setSubmitting(false)
    }
  }

  const handleAddReply = async (parentId: number) => {
    if (!contentId || !replyText.trim() || target !== 'slop') return
    setSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(contentId, replyText.trim(), parentId)
      const next = comments.map((c) =>
        c.id === parentId
          ? { ...c, replies: [...(c.replies ?? []), {
            id: created.id,
            body: created.body,
            authorUsername: created.authorUsername,
            parentCommentId: created.parentCommentId,
            createdAt: created.createdAt,
            replies: []
          }] }
          : c
      )
      setComments(next)
      notifyCount(next)
      setReplyText('')
      setReplyingToId(null)
    } catch { /* */ } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (commentId: number, parentId?: number) => {
    if (!contentId) return
    try {
      if (target === 'slop') {
        await agent.SlopFeed.deleteComment(contentId, commentId)
      } else {
        await agent.Shoots.deleteComment(contentId, commentId)
      }
      const next = parentId != null
        ? comments.map((c) =>
          c.id === parentId
            ? { ...c, replies: (c.replies ?? []).filter((r) => r.id !== commentId) }
            : c
        )
        : comments.filter((c) => c.id !== commentId)
      setComments(next)
      notifyCount(next)
    } catch { /* */ }
  }

  const canDelete = (username?: string | null) =>
    !!username && (username === currentUser?.username || isAdmin)

  const renderActions = (comment: PlatformCommentNode, depth: number) => (
    <Group spacing={4} mt={4}>
      {supportsReplies && depth === 0 && isLoggedIn && (
        <AppButton
          variant="subtle"
          size="xs"
          compact
          leftIcon={<IoReturnDownForwardOutline size={12} />}
          onClick={() => setReplyingToId(replyingToId === comment.id ? null : comment.id)}
        >
          Reply
        </AppButton>
      )}
      {canDelete(comment.authorUsername) && (
        <AppIconButton
          variant="subtle"
          size="sm"
          color="gray"
          aria-label="Delete comment"
          onClick={() => handleDelete(comment.id, depth > 0 ? comment.parentCommentId ?? undefined : undefined)}
        >
          <IoTrashOutline size={14} />
        </AppIconButton>
      )}
    </Group>
  )

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Comments"
      position="bottom"
      size="70%"
      padding="md"
      zIndex={400}
    >
      <Stack spacing="sm" sx={{ height: '100%' }}>
        {!contentId
          ? (
            <AppText size="sm" color="dimmed">No content selected.</AppText>
            )
          : (
            <>
              {loadError && (
                <Alert color="red" variant="light">{loadError}</Alert>
              )}

              {isLoggedIn
                ? (
                  <CommentInputRow
                    input={{
                      placeholder: 'Add a comment…',
                      value: newComment,
                      onChange: (e) => setNewComment(e.currentTarget.value),
                      autosize: true,
                      minRows: 1,
                      maxRows: 3,
                      disabled: submitting
                    }}
                    action={(
                      <AppButton
                        variant="light"
                        size="sm"
                        leftIcon={<IoSend size={14} />}
                        loading={submitting}
                        disabled={!newComment.trim()}
                        onClick={handleAddTopLevel}
                      >
                        Post
                      </AppButton>
                    )}
                  />
                  )
                : (
                  <AppText size="sm" color="dimmed">
                    <AppText
                      component="span"
                      sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => history.push('/login')}
                    >
                      Log in
                    </AppText>
                    {' '}to comment.
                  </AppText>
                  )}

              <ScrollArea sx={{ flex: 1 }} offsetScrollbars>
                <OneLevelCommentList
                  comments={comments}
                  loading={loading}
                  emptyMessage="No comments yet. Be the first!"
                  renderActions={renderActions}
                />
                {supportsReplies && replyingToId != null && (
                  <Box mt="xs" pl="md">
                    <CommentInputRow
                      input={{
                        placeholder: 'Write a reply…',
                        value: replyText,
                        onChange: (e) => setReplyText(e.currentTarget.value),
                        autosize: true,
                        minRows: 1,
                        maxRows: 2,
                        disabled: submitting
                      }}
                      action={(
                        <AppButton
                          size="xs"
                          variant="light"
                          loading={submitting}
                          disabled={!replyText.trim()}
                          onClick={() => handleAddReply(replyingToId)}
                        >
                          Reply
                        </AppButton>
                      )}
                    />
                  </Box>
                )}
              </ScrollArea>

              <Group position="apart">
                <AppText size="xs" color="dimmed">
                  {totalCount} comment{totalCount !== 1 ? 's' : ''}
                </AppText>
                {fullPostPath && (
                  <AppText
                    size="xs"
                    sx={{ color: 'var(--gs-accent)', cursor: 'pointer' }}
                    onClick={() => {
                      onClose()
                      history.push(fullPostPath)
                    }}
                  >
                    Open full post
                  </AppText>
                )}
              </Group>
            </>
            )}
      </Stack>
    </AppDrawer>
  )
}

export default CommentsDrawer
